import {definePreset} from '@primeng/themes';
import {AuraBaseDesignTokens} from '@primeng/themes/aura/base';
import Aura from '@primeng/themes/aura';
import {Preset} from '@primeng/themes/types';

const CustomTokens: Preset<AuraBaseDesignTokens> = {
    css: `
    :root {
      /* Font stack */
      --app-font-sans: "Helvetica Neue 55 Roman", "Helvetica Neue", sans-serif;
      --scale-menu:           0.93rem;
      --scale-user-name:      0.87rem;
      --scale-user-subtitle:  0.87rem;
      --scale-breadcrumb:     0.87rem;
      --bs-body-font-weight: 500;
      --bs-border-radius: 0;
      --bs-link-color-rgb: 0, 0, 0;
      --bs-link-hover-color-rgb: 255, 121, 0;
      --p-breadcrumb-item-hover-color: rgba(255, 121, 0, 1);
      --tab-ruler-color: #F16E00;
    }

    html, body, .p-component { font-family: var(--app-font-sans); }
    a {
        cursor: pointer;
        display: inline-flex;
    }

    .pi {
        font-weight: 550;
    }
    .pi:focus-visible {
        outline: none !important;
    }
    input,
    textarea,
    .p-inputtext,
    .p-password,
    .p-select,
    .p-multiselect,
    .p-datepicker,
    .p-treeselect,
    p-columnfilterformelement {
        width: 100% !important;
    }
    .row {
        --bs-gutter-x: 0;
        --bs-gutter-y: 0;
    }

    .pi {
        display: inline-block;
        line-height: normal !important;
        font-size: 1em;
        align-self: center;
    }
    .pi::before {
        display: inline-block;
        line-height: normal !important;
    }

    .p-button,
    .p-menuitem-link,
    .p-breadcrumb .p-menuitem-link,
    .p-chip, .p-tag {
        height:100% !important;
        display: inline-flex;
        align-items: center !important;
        gap: .5rem;
    }

    .p-buttongroup .p-button,
    .p-panel .p-panel-header .p-buttongroup .p-button {
        display: inline-flex;
        align-items: center;
        height: 100% !important;
    }
    .p-panel-title {
        font-size: 1.25rem;
    }
    .p-icon {
        display: inline-block;
        vertical-align: middle;
        transform: translateY(0.02em);
    }

    /* DataTable: Remove frozen column background */
    .p-datatable-scrollable td.p-datatable-frozen-column {
        background: unset !important;
    }
    `,

    primitive: {
        borderRadius: {
            none: '0',
            xs: '0',
            sm: '0',
            md: '0',
            lg: '0',
            xl: '0'
        }
    },
    semantic: {
        transitionDuration: 'none',
        primary: {
            50: '{orange.50}',
            100: '{orange.100}',
            200: '{orange.200}',
            300: '{orange.300}',
            400: '{orange.400}',
            500: '{orange.500}',
            600: '{orange.600}',
            700: '{orange.700}',
            800: '{orange.800}',
            900: '{orange.900}',
            950: '{orange.950}'
        },
        colorScheme: {
            light: {
                content: {
                    color: '#000000',
                    background: '#FFFFFF',
                    borderColor: '#CCCCCC',
                    hoverBackground: '#e9ecef',
                    hoverColor: '#000000'
                },
                primary: {
                    color: '#FF7900',
                    activeColor: '#FF7900'
                },
                list: {
                    option: {
                        focusBackground: '#CCCCCC',
                        selectedBackground: '#000000',
                        selectedFocusBackground: '#000000',
                        color: '{text.color}',
                        focusColor: '#000000',
                        selectedColor: '#FFFFFF',
                        selectedFocusColor: '#FFFFFF'
                    }
                },
                navigation: {
                    item: {
                        focusBackground: 'rgba(0,0,0,0)',
                        activeBackground: 'rgba(0,0,0,0)',
                        color: '#FFFFFF',
                        focusColor: '{primary.color}',
                        activeColor: '{primary.color}',
                        icon: {
                            color: '#FFFFFF',
                            focusColor: '{primary.color}',
                            activeColor: '{primary.color}'
                        }
                    },
                    submenuLabel: {
                        background: 'transparent',
                        color: '#FFFFFF'
                    },
                    submenuIcon: {
                        color: '#FFFFFF',
                        focusColor: '{primary.color}',
                        activeColor: '{primary.color}'
                    }
                },
                formField: {
                    filledBackground: '#FFFFFF'
                },
                overlay: {
                    select: {
                        background: '#EEEEEE',
                        borderColor: '#666666'
                    }
                },
                text: {
                    color: '#000000',
                    mutedColor: '#FFFFFF'
                }
            }
        },
        focusRing: {width: '0.06rem', style: 'solid', color: '{primary.color}', offset: '0.12rem', shadow: 'none'},
        formField: {
            paddingX: '0.75rem',
            paddingY: '0.5rem',
            borderRadius: 'none',
            sm: {
                fontSize: '0.875rem',
                paddingX: '0.375rem',
                paddingY: '0.25rem'
            },
            lg: {
                fontSize: '1.125rem',
                paddingX: '0.875rem',
                paddingY: '0.625rem'
            }
        },
        list: {
            gap: 'none'
        },
        navigation: {
            list: {
                padding: '0.25rem 0.25rem',
                gap: '0.12rem'
            },
            item: {
                padding: '0.5rem 0.10rem',
                borderRadius: '{border.radius.sm}',
                gap: '0.25rem'
            },
            submenuLabel: {
                padding: '0.5rem 0.75rem',
                fontWeight: '600'
            },
            submenuIcon: {
                size: '0.875rem'
            }
        },
        overlay: {
            modal: {
                padding: '0.5rem'
            }
        }
    },
    components: {
        avatar: {
            root: {
                background: '#FFFFFF'
            }
        },
        breadcrumb: {
            css: `
                .p-breadcrumb-list {
                    padding-top: 0.5rem;
                }
                .p-breadcrumb-item:not(:last-child) {
                    font-size: var(--scale-breadcrumb);
                    font-family: "Helvetica Neue 75 Bold", "Helvetica Neue 55 Roman", "Helvetica Neue", sans-serif;
                }
                .p-breadcrumb-item:last-child {
                    font-size: var(--scale-breadcrumb);
                    font-family: "Helvetica Neue 55 Roman", "Helvetica Neue", sans-serif;
                }
            `,
            root: {
                padding: '0.25rem 1rem 0.5rem 1rem',
                gap: '0.5rem'
            },
            item: {
                hoverColor: '{primary.color}',
                color: '{content.color}'
            },
            separator: {
                color: '{content.color}'
            }
        },
        button: {
            root: {
                gap: '0.5rem',
                paddingX: '1.125rem',
                paddingY: '0.5rem',
                borderRadius: '0',
                label: {
                    fontWeight: '600'
                },
                primary: {
                    color: '#FFFFFF',
                    hoverColor: '#FFFFFF',
                    activeColor: '#FFFFFF',

                    background: '#000000',
                    hoverBackground: '#666666',
                    activeBackground: '#000000'
                },
                contrast: {
                    color: '#000000',
                    hoverColor: '#000000',
                    activeColor: '#000000',

                    background: '#FFFFFF',
                    hoverBackground: '#DDDDDD',
                    activeBackground: '#FFFFFF',
                    borderColor: '#000000',
                    hoverBorderColor: '#000000',
                    activeBorderColor: '#000000'
                },
                secondary: {
                    color: '{content.color}',
                    hoverColor: '#999999',
                    background: '{content.background.color}'
                },
                success: {
                    color: '#FFFFFF',
                    hoverColor: '#FFFFFF',
                    activeColor: '#000000',

                    background: '#32c832',
                    hoverBackground: '#000000',
                    activeBackground: '{primary.color}',

                    borderColor: '#32c832',
                    hoverBorderColor: '#000000',
                    activeBorderColor: '{primary.color}'
                },
                danger: {
                    color: '#FFFFFF',
                    hoverColor: '#FFFFFF',
                    activeColor: '#000000',

                    background: '#cd3c14',
                    hoverBackground: '#000000',
                    activeBackground: '{primary.color}',

                    borderColor: '#cd3c14',
                    hoverBorderColor: '#000000',
                    activeBorderColor: '{primary.color}'
                },
                warn: {
                    color: '{content.background}',
                    hoverColor: '{content.background}',
                    activeColor: '{content.color}',

                    background: '#fc0',
                    hoverBackground: '{content.color}',
                    activeBackground: '{primary.color}',

                    borderColor: '#fc0',
                    hoverBorderColor: '{content.color}',
                    activeBorderColor: '{primary.color}'
                }
            },
            link: {
                color: '#000000',
                hoverColor: '{primary.color}'
            },
            colorScheme: undefined
        },
        checkbox: {
            css: `
                p-checkbox {
                    //line-height: 1.35rem !important;
                }
                .p-checkbox-box {
                    border: 0.12rem solid var(--p-checkbox-border-color);
                }
            `,
            root: {
                checkedBorderColor: '{form.field.border.color}',
                checkedHoverBorderColor: '{form.field.hover.border.color}',
                checkedFocusBorderColor: '{form.field.hover.border.color}'
            },
            icon: {
                checkedColor: '#000000'
            }
        },
        confirmdialog: {
            css: `
                .p-confirmdialog .p-dialog-footer {
                    padding: var(--p-dialog-content-padding);
                }
            `
        },
        contextmenu: {
            item: {
                color: '{text.color}',
                focusBackground: '#EEEEEE',
                icon: {
                    color: '{text.color}'
                }
            },
            submenuIcon: {
                color: '{text.color}'
            }
        },
        datatable: {
            header: {
                background: '#FFFFFF',
                borderWidth: '0',
                color: '#000000',
                padding: '0.75rem 0rem',
                sm: {
                    padding: '0.375rem 0rem'
                },
                lg: {
                    padding: '1rem 0rem'
                }
            },
            headerCell: {
                background: '{overlay.select.background}',
                selectedBackground: '{overlay.select.background}',
                padding: '0.5rem 0.5rem',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                selectedColor: '{highlight.color}',
                gap: '0.5rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '-0.06rem',
                    shadow: '{focus.ring.shadow}'
                },
                sm: {
                    padding: '0.375rem 0.5rem'
                },
                lg: {
                    padding: '1rem 1.25rem'
                }
            },
            columnTitle: {
                fontWeight: '600'
            },
            row: {
                background: '{content.background}',
                hoverBackground: '{content.hover.background}',
                selectedBackground: '{highlight.background}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                selectedColor: '{highlight.color}',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '-0.06rem',
                    shadow: '{focus.ring.shadow}'
                }
            },
            bodyCell: {
                padding: '0.5rem 0.5rem',
                borderColor: '{datatable.border.color}',
                sm: {
                    padding: '0.375rem 0.5rem'
                },
                lg: {
                    padding: '1rem 1.25rem'
                }
            },
            footerCell: {
                background: '{content.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                padding: '0.75rem 1rem',
                sm: {
                    padding: '0.375rem 0.5rem'
                },
                lg: {
                    padding: '1rem 1.25rem'
                }
            },
            columnFooter: {
                fontWeight: '600'
            },
            footer: {
                background: '{content.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                borderWidth: '0 0 0.06rem 0',
                padding: '0.75rem 1rem',
                sm: {
                    padding: '0.375rem 0.5rem'
                },
                lg: {
                    padding: '1rem 1.25rem'
                }
            },
            dropPoint: {
                color: '{primary.color}'
            },
            columnResizer: {
                width: '0.5rem'
            },
            resizeIndicator: {
                width: '0.06rem',
                color: '{primary.color}'
            },
            sortIcon: {
                color: '{text.muted.color}',
                hoverColor: '{text.hover.muted.color}',
                size: '0.875rem'
            },
            loadingIcon: {
                size: '2rem'
            },
            rowToggleButton: {
                hoverBackground: '{content.hover.background}',
                selectedHoverBackground: '{content.background}',
                color: '{text.muted.color}',
                hoverColor: '{text.color}',
                selectedHoverColor: '{primary.color}',
                size: '1.75rem',
                borderRadius: '50%',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                }
            },
            filter: {
                inlineGap: '0.5rem',
                overlaySelect: {
                    background: '{overlay.select.background}',
                    borderColor: '{overlay.select.border.color}',
                    borderRadius: '{overlay.select.border.radius}',
                    color: '{overlay.select.color}',
                    shadow: '{overlay.select.shadow}'
                },
                overlayPopover: {
                    background: '{overlay.popover.background}',
                    borderColor: '{overlay.popover.border.color}',
                    borderRadius: '{overlay.popover.border.radius}',
                    color: '{overlay.popover.color}',
                    shadow: '{overlay.popover.shadow}',
                    padding: '{overlay.popover.padding}',
                    gap: '0.5rem'
                },
                rule: {
                    borderColor: '{content.border.color}'
                },
                constraintList: {
                    padding: '{list.padding}',
                    gap: '{list.gap}'
                },
                constraint: {
                    focusBackground: '{list.option.focus.background}',
                    selectedBackground: '{list.option.selected.background}',
                    selectedFocusBackground: '{list.option.selected.focus.background}',
                    color: '{list.option.color}',
                    focusColor: '{list.option.focus.color}',
                    selectedColor: '{list.option.selected.color}',
                    selectedFocusColor: '{list.option.selected.focus.color}',
                    separator: {
                        borderColor: '{content.border.color}'
                    },
                    padding: '{list.option.padding}',
                    borderRadius: '{list.option.border.radius}'
                }
            },
            paginatorTop: {
                borderColor: '{datatable.border.color}',
                borderWidth: '0 0 0.06rem 0'
            },
            paginatorBottom: {
                borderColor: '{datatable.border.color}',
                borderWidth: '0 0 0.06rem 0'
            },
            colorScheme: {
                light: {
                    root: {
                        borderColor: '{content.border.color}'
                    },
                    row: {
                        stripedBackground: '{surface.50}'
                    },
                    bodyCell: {
                        selectedBorderColor: '{primary.100}'
                    }
                },
                dark: {
                    root: {
                        borderColor: '{surface.800}'
                    },
                    row: {
                        stripedBackground: '{surface.950}'
                    },
                    bodyCell: {
                        selectedBorderColor: '{primary.900}'
                    }
                }
            }
        },
        card: {
            title: {
                fontSize: '1.5rem',
                fontWeight: '700'
            }
        },
        chip: {
            root: {
                borderRadius: 'none',
                paddingX: '0.35rem',
                paddingY: '0.35rem'
            }
        },
        dialog: {
            css: `
                .p-dialog {
                    border: 0.12rem solid var(--p-dialog-border-color);

                    .p-dialog-content {
                        display: flex !important;
                    }
                }
            `,
            footer: {
                padding: '{overlay.modal.padding} 0 0 0'
            }
        },
        editor: {
            toolbar: {
                background: '{content.color}'
            },
            toolbarItem: {
                color: '{content.background}',
                hoverColor: '{primary.color}',
                activeColor: '{primary.color}'
            }
        },
        megamenu: {
            css: `
                .p-megamenu-horizontal  {
                    border: none;
                    align-items: flex-end;
                    width: 100%;
                }
                div.p-megamenu-overlay {
                    left: unset;
                    min-width: unset;
                    z-index: 100;
                }
                .p-megamenu-submenu-label {
                    font-size: var(--scale-menu);
                    border-bottom: 0.06rem white solid;
                }
                .p-megamenu-item-link-active, .p-megamenu-item-link-active .pi {
                    color: var(--p-primary-color);
                    font-weight: bold;
                }
            `,
            root: {
                background: '#000000',
                gap: '0.5rem',
                horizontalOrientation: {
                    padding: '0.5rem 1rem'
                },
                borderRadius: 'none'
            },
            baseItem: {
                padding: '0.5rem 0.35rem 0.1rem 0.35rem'
            },
            overlay: {
                padding: '0',
                background: '#000000'
            }
        },
        menu: {
            root: {
                background: '{content.background}',
                color: '{content.color}'
            },
            item: {
                color: '{content.color}',
                icon: {
                    color: '{content.color}'
                }
            }
        },
        menubar: {
            css: `
                .p-menubar  {
                    border: None
                }
            `,
            root: {
                background: '#000000',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem'
            },
            submenu: {
                background: '#000000'
            }
        },
        multiselect: {
            css: `
                .p-multiselect-option {
                    font-weight: var(--bs-body-font-weight);
                }
            `
        },
        metergroup: {
            css: `
                .p-metergroup-meters {
                    height: 0.75rem !important;
                }
            `
        },
        paginator: {
            css: `
                .p-paginator .p-select {
                    width: initial !important;
                }
            `,
            root: {
                padding: '0.5rem 1rem',
                gap: '0.25rem',
                borderRadius: '{content.border.radius}',
                background: '{content.background}',
                color: '{content.color}',
                transitionDuration: '{transition.duration}'
            },
            navButton: {
                borderRadius: 'none',
                color: '{content.color}',
                background: '{content.background}',

                hoverColor: '{content.background}',
                hoverBackground: '{content.color}',

                selectedBackground: '{primary.color}',
                selectedColor: '{content.color}'
            }
        },
        panel: {
            css: `
                .p-panel {
                    border-width: 0px;

                    & > .p-panel-header {
                        justify-content: space-between;
                        align-items: center;
                        display: flex;
                        width: 100% !important;

                        .p-panel-title {
                            gap: 0.5rem;
                            align-items: center;
                            display: flex;
                        }
                    }
                }
                p-panel.w-full {
                    width: 100%;
                }
                p-panel.plain-header > .p-panel {
                    & > .p-panel-header {
                        background-color: #f4f4f4;
                        border-bottom-left-radius: 0;
                        border-bottom-right-radius: 0;
                        padding-bottom: 1rem;
                    }
                }
                p-panel.title-center > .p-panel > .p-panel-header {
                    justify-content: center;
                }
                p-panel.no-frame {
                    .p-panel-header { display: none; }
                    .p-panel { border: none !important; }
                    .p-panel-content { padding: 0; }
                }
                p-panel.borderless > .p-panel {
                    border: none !important;
                    box-shadow: none !important;
                }
                p-panel.with-borders > .p-panel {
                    border-width: 1px;
                }
                p-panel.no-header > .p-panel {
                    & > .p-panel-header {
                        display: none;
                    }
                }
                p-panel.no-x-padding > .p-panel {
                    & > .p-panel-header {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    & > .p-panel-content-container {
                        & > .p-panel-content {
                            padding-left: 0 !important;
                            padding-right: 0 !important;
                        }
                    }
                }
            `,
            header: {
                background: '{content.background}',
                color: '{content.color}',
                padding: '1rem 0.5rem 0.1rem 0.5rem',
                borderColor: '#CCCCCC',
                borderRadius: '{border.radius.md}'
            },
            content: {
                padding: '0.5rem 0.5rem'
            },
            footer: {
                padding: '0.5rem 0.5rem'
            }
        },
        progressspinner: {
            colorScheme: {
                light: {
                    root: {
                        colorOne: '{primary.color}',
                        colorTwo: '{primary.color}',
                        colorThree: '{primary.color}',
                        colorFour: '{primary.color}'
                    }
                },
                dark: {
                    root: {
                        colorOne: '{primary.color}',
                        colorTwo: '{primary.color}',
                        colorThree: '{primary.color}',
                        colorFour: '{primary.color}'
                    }
                }
            }
        },
        stepper: {
            stepTitle: {
                color: '#000000'
            },
            stepNumber: {
                color: '#000000'
            }
        },
        tabs: {
            css: `
                .p-tablist-active-bar {
                    background-color: #f16e00 !important;
                    height: 0.25rem !important;
                }
            `,
            tab: {
                padding: '0.5rem'
            },
            tabpanel: {
                padding: '1rem 0rem 0rem 0rem'
            },
            colorScheme: {
                light: {
                    tab: {
                        color: '{content.color}',
                        activeColor: '{content.color}',
                        hoverColor: '{primary.color}'
                    }
                }
            }
        },
        tag: {
            css: `
                .p-tag {
                    position: relative;
                    font-size: 0.875rem;
                    font-weight: 500;
                }
            `,
            colorScheme: {
                light: {
                    primary: {
                        color: '#000000',
                        background: '{primary.color}'
                    },
                    secondary: {
                        color: '#FFFFFF',
                        background: '#000000'
                    },
                    success: {
                        color: '#FFFFFF',
                        background: '#32c832'
                    },
                    info: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    },
                    warn: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    },
                    danger: {
                        color: '#FFFFFF',
                        background: '#cd3c14'
                    },
                    contrast: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    }
                },
                dark: {
                    primary: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    },
                    secondary: {
                        color: '#FFFFFF',
                        background: '#000000'
                    },
                    success: {
                        color: '#FFFFFF',
                        background: '#32c832'
                    },
                    info: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    },
                    warn: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    },
                    danger: {
                        color: '#FFFFFF',
                        background: '#cd3c14'
                    },
                    contrast: {
                        color: '{content.background}',
                        background: '{primary.color}'
                    }
                }
            }
        },
        tree: {
            css: `
                .menu .side .p-tree {
                    background: none;

                    .p-tree-node-content {
                        --bs-link-color-rgb: 255, 255, 255;
                        color: white;

                        a {
                            text-decoration: none;
                        }
                        .p-tree-node-toggle-button {
                            color: #FFFFFF;
                        }
                        .p-tree-node-icon {
                            color: #FFFFFF;
                        }

                        &:hover {
                            .p-tree-node-icon {
                                color: var(--p-primary-color);
                            }
                            a {
                                color: var(--p-primary-color);
                            }
                        }

                        .p-tree-node-toggle-button:hover {
                            color: black;
                            background: white;
                        }
                    }
                }

                .p-tree-node-content {
                    align-items: stretch;
                }

                .p-tree-node-label {
                    display: flex;
                    align-items: center;
                }
            `,
            root: {
                padding: 'none',
                gap: '0.1rem'
            },
            node: {
                selectedColor: '{primary.color}',
                selectedBackground: 'none',
                hoverColor: '{text.color}',
                hoverBackground: '{list.option.focusBackground}',
                gap: '0.5rem',
                padding: '0.20rem 0rem 0.20rem 0rem'
            },
            nodeIcon: {
                color: '{text.color}',
                hoverColor: '{text.hover.muted.color}',
                selectedColor: '{highlight.color}'
            },
            nodeToggleButton: {
                color: '{text.color}',
                hoverColor: '{text.mutedColor}',
                hoverBackground: '{text.color}',
                selectedHoverColor: '{text.mutedColor}',
                selectedHoverBackground: '{text.color}',
                borderRadius: '{border.radius.md}'
            }
        },
        treetable: {
            css: `
                .p-treetable.p-treetable-striped .p-treetable-tbody > tr:nth-child(odd) {
                    background: var(--p-surface-50);
                }
                .p-treetable .p-treetable-thead>tr>th {
                    text-align: start !important;
                }
                .p-treetable.p-treetable-sm .p-treetable-header {
                    padding: 0.75rem 0rem;
                }
                .p-treetable .p-treetable-tbody>tr.p-treetable-row-selected {
                    background: var(--p-treetable-row-selected-background) !important;
                }
                .p-treetable-tbody > tr:focus-visible, .p-treetable-tbody > tr.p-treetable-contextmenu-row-selected {
                    outline: none !important;
                }
            `,
            header: {
                background: '#FFFFFF',
                borderWidth: '0',
                color: '#000000',
                padding: '0.75rem 0rem'
            },
            nodeToggleButton: {
                color: '{text.color}'
            },
            loadingIcon: {
                size: '2rem'
            }
        },
        accordion: {
            header: {
                background: '{overlay.select.background}',
                borderColor: '#000000',
                color: '#FFFFFF'
            }
        }
    }
};

export const OrangePrimeNGTheme = definePreset(Aura, CustomTokens);
